import { useTranslation } from 'react-i18next';
import CustomRadio from '../../../../../components/CustomRadio';
import CustomRadioGroup from '../../../../../components/CustomRadioGroup';
import { CustomSelect } from '../../../../../components/CustomSelect';
import { CustomTextField } from '../../../../../components/CustomTextField';
import DividerWithText from '../../../../../components/DividerWithText';
import {
    BASE_COST_NAME, COST_TYPE_NAME,
    FOODIE_SETTING_MODEL, IS_BASE_COST_NAME, IS_CATALOGUE_COST_NAME, IS_MEAL_TIME_COST_NAME, MIN_MEAL_COST,
    MODE_TYPE_MODEL, RECORD_TIMEOUT_LIMITS
} from '../Models/Model';
import { colors } from '../../../../../enums/colors';
import React from "react";
import { permissionObj } from '../../../../../Helpers/permission';
import { useSelector } from 'react-redux';

/**
 *
 * @Author : Jhovanny Fragoso
 * @JobTitle : Frontend Developer
 * @Email : jhovanny@ibl.mx
 *
 * @param {Object}   item                        - Foodie setting model containing all configuration fields.
 * @param {Function} onChange                    - Generic field change handler.
 * @param {boolean}  emptyFields                 - Whether to highlight empty required fields.
 * @param {string}   pricePlaceholder            - Placeholder text for cost/price inputs.
 * @param {boolean}  isValidSelectionMenuDelay   - Whether the selection menu delay value is valid.
 * @param {boolean}  isValidRecordTimeout        - Whether the minimum time between entries value is valid.
 * @param {string}   recordTimeoutRangeText      - Message describing the allowed range for the minimum time between entries.
 * @param {boolean}  isValidCost                 - Whether the base meal cost value is valid.
 * @param {string}   isValidCostText             - Error message key shown when base meal cost is invalid.
 * @param {boolean}  isValidSubsidy              - Whether the subsidy value is valid.
 * @param {string}   isValidSubsidyText          - Error message key shown when subsidy is invalid.
 * @param {boolean}  isValidExtraMealSubsidy     - Whether the extra meal subsidy value is valid.
 * @param {string}   isValidExtraMealSubsidyText - Error message key shown when extra meal subsidy is invalid.
 * @param {Function} onCostChange                - Handler for meal pricing radio group changes.
 * @returns {JSX.Element}
 */

const FoodieSetting = ({
    item = FOODIE_SETTING_MODEL,
    onChange,
    emptyFields,
    pricePlaceholder,
    isValidSelectionMenuDelay,
    isValidRecordTimeout,
    recordTimeoutRangeText,
    isValidCost,
    isValidCostText,
    isValidSubsidy,
    isValidSubsidyText,
    isValidExtraMealSubsidy,
    isValidExtraMealSubsidyText,
    onCostChange
}) => {
    const { t } = useTranslation();
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);

    return (
        <>
            <DividerWithText spacing="my-4" title={t('restrictions')} />

            {/* Security mode & Attendance mode */}
            <div className="row">
                <div className="col-sm-12 col-md-6 p-0">
                    <CustomSelect
                        id="slt_fs_security_mode"
                        trans="security_mode"
                        list={[...MODE_TYPE_MODEL]}
                        keyName="id"
                        name="securityModeId"
                        value={item.securityModeId || ''}
                        onChange={onChange}
                        required
                        emptyFields={emptyFields}
                        showNoSelectionOption={false}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                    />
                </div>
                <div className="col-sm-12 col-md-6 mb-3 p-0 px-md-2">
                    <CustomSelect
                        id="slt_fs_attendance_mode"
                        trans="attendance_mode"
                        list={[...MODE_TYPE_MODEL]}
                        keyName="id"
                        name="accessModeId"
                        value={item.accessModeId || ''}
                        onChange={onChange}
                        required
                        emptyFields={emptyFields}
                        showNoSelectionOption={false}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                    />
                </div>
            </div>

            {/* Meal Pricing */}
            <div className='row row-cols-md-2'>
                <CustomRadioGroup trans={t('meal_pricing')} classTitle="fs-14px ibl-font-w-600 font-color-general-950">
                    <CustomRadio
                        id="rbtn_fs_meal_catalogue_price"
                        trans="meal_catalogue_price"
                        value={IS_CATALOGUE_COST_NAME}
                        name={COST_TYPE_NAME}
                        checked={item.isBaseCost === false && item.isMealTimeCost === false}
                        onChange={onCostChange}
                        color={colors.primary800}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                    />

                    <CustomRadio
                        id="rbtn_fs_meal_time_price"
                        trans="meal_time_price"
                        value={IS_MEAL_TIME_COST_NAME}
                        name={COST_TYPE_NAME}
                        checked={item.isBaseCost === false && item.isMealTimeCost === true}
                        onChange={onCostChange}
                        color={colors.primary800}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                    />

                    <div className='row'>
                        <div className='col-sm-12 col-md-6 p-0'>
                            <CustomRadio
                                id="rbtn_fs_general_meal_price"
                                trans="general_meal_price"
                                value={IS_BASE_COST_NAME}
                                name={COST_TYPE_NAME}
                                checked={item.isBaseCost === true && item.isMealTimeCost === false}
                                onChange={onCostChange}
                                color={colors.primary800}
                                disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                            />
                        </div>

                        <div className='col-sm-12 col-md-6 p-0'>

                            <CustomTextField
                                id="txt_fs_meal_cost"
                                trans="meal_cost"
                                type="text"
                                name={BASE_COST_NAME}
                                value={item.baseCost}
                                onChange={onChange}
                                required
                                emptyFields={emptyFields}
                                disabled={item.isBaseCost === false || !(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                                length={8}
                                placeholder={pricePlaceholder}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>$</p>}
                                decimals={2}
                                isValid={isValidCost}
                                isValidText={isValidCostText}
                                helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MEAL_COST.min })}
                            />
                        </div>

                    </div>

                </CustomRadioGroup>
            </div>

            {/* Subsidy */}
            <h5 className="fs-14px ibl-font-w-600 mb-4 font-color-general-950">{t('subsidy_criteria')}</h5>
            <div className="row mb-3">
                <div className="col-12 col-md-3 p-0">
                    <CustomTextField
                        id="txt_fs_subsidy"
                        trans="subsidy"
                        type="text"
                        name="subsidy"
                        value={item.subsidy}
                        onChange={onChange}
                        required
                        emptyFields={emptyFields}
                        length={8}
                        placeholder={pricePlaceholder}
                        suffixText={<p className={"ibl-font-w-600 text-uppercase"}>$</p>}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                        decimals={2}
                        isValid={isValidSubsidy}
                        isValidText={isValidSubsidyText}
                        helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MEAL_COST.min })}
                    />
                </div>
            </div>

            {/* Extra Meal Subsidy */}
            <div className='row row-cols-md-2'>
                <CustomRadioGroup trans={t('extra_meal_subsidy')} classTitle="fs-14px ibl-font-w-600 font-color-general-950">
                    <CustomRadio
                        id="rbtn_fs_same_as_regular"
                        trans="same_as_regular"
                        value={false}
                        name="isExtraMealSubsidy"
                        checked={item.isExtraMealSubsidy === false}
                        onChange={onChange}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                    />


                    {/* Radio: custom_subsidy + input */}
                    <div className="row">
                        <div className="col-sm-12 col-md-6 p-0">
                            <CustomRadio
                                id="rbtn_fs_custom_subsidy"
                                trans="custom_subsidy"
                                value={true}
                                name="isExtraMealSubsidy"
                                checked={item.isExtraMealSubsidy === true}
                                onChange={onChange}
                                disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                            />
                        </div>
                        <div className="col-sm-12 col-md-6 p-0">
                            <CustomTextField
                                id="txt_fs_meal_subsidy"
                                trans="subsidy"
                                type="text"
                                name="extraMealSubsidy"
                                value={item.extraMealSubsidy}
                                onChange={onChange}
                                required
                                emptyFields={item.isExtraMealSubsidy ? emptyFields : false}
                                length={8}
                                placeholder={pricePlaceholder}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>$</p>}
                                disabled={item.isExtraMealSubsidy === false || !(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                                decimals={2}
                                isValid={isValidExtraMealSubsidy}
                                isValidText={isValidExtraMealSubsidyText}
                                helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MEAL_COST.min })}
                            />
                        </div>
                    </div>
                </CustomRadioGroup>
            </div>

            {/* Selection Menu Delay */}
            <h5 className="fs-14px ibl-font-w-600 mb-4 font-color-general-950">{t('selection_menu_delay')}</h5>
            <div className="row mb-3">
                <div className="col-12 col-md-3 p-0">
                    <CustomTextField
                        id="txt_fs_selection_menu_delay"
                        trans="minutes"
                        type="number"
                        name="selectionMenuDelay"
                        value={item.selectionMenuDelay}
                        onChange={onChange}
                        required
                        emptyFields={emptyFields}
                        length={5}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                        isValid={isValidSelectionMenuDelay}
                        isValidText={"this_value_must_be_greater_than_0"}
                        helperText={"this_value_must_be_greater_than_0"}
                        suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                    />
                </div>
            </div>

            {/* Minimum time between entries */}
            <h5 className="fs-14px ibl-font-w-600 mb-4 font-color-general-950">{t('minimum_time_between_entries')}</h5>
            <div className="row mb-3">
                <div className="col-12 col-md-3 p-0">
                    <CustomTextField
                        id="txt_fs_record_timeout"
                        trans="minutes"
                        type="number"
                        name="recordTimeout"
                        value={item.recordTimeout}
                        onChange={onChange}
                        required
                        emptyFields={emptyFields}
                        length={String(RECORD_TIMEOUT_LIMITS.MAX).length}
                        disabled={!(DEV || permission?.includes(permissionObj?.WEB_FOODIE_MANAGE_SETTINGS_UPDATE))}
                        isValid={isValidRecordTimeout}
                        isValidText={recordTimeoutRangeText}
                        helperText={recordTimeoutRangeText}
                        suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                    />
                </div>
            </div>
        </>
    );
};

export default FoodieSetting;
